import React, { useState } from "react";
import { Eye, TriangleAlert, UserPlus, X } from "lucide-react";
import { Button } from "../ui/button";
import { ItemList, ItemListColumn, ItemListRow } from "../itemList";

import FilterPeopleModal from "@/components/filterPeople";
import EditorPreview from "@/components/tiptap-editor/editorPreview";

import useUpdateUserComm from "@/hooks/notifications/useUpdateUserComm";
import useUserComm from "@/hooks/notifications/useUserComm";
import usePagination from "@/hooks/usePagination";
import useDialogModal from "@/hooks/useDialogModal";
import useSendEmailJobRecords from "@/hooks/notifications/useSendEmailJobRecords";

import i18n from "@/i18n";
import { getPlaceholderFieldsConfig } from "../tiptap-editor/extensions/placeholder";
import useLatestShareData from "@/hooks/shares/useLatestShareData";
import Avatar from "../avatar";

const { t } = i18n;

const UserCommRecipientList = ({ editor, userCommId, disabled }) => {
  const { confirm, dialogElement } = useDialogModal();

  const { data: userCommQuery } = useUserComm(userCommId);
  const userComm = userCommQuery?.data;
  const recipientIds = userComm?.recipients.map((r) => r.id) ?? [];

  const {
    mutateAsync: updateUserComm,
    variables: updateUserCommVariables,
    isPending: isPendingUpdateUserComm,
  } = useUpdateUserComm(userCommId);

  const [showFilterPeopleModal, setShowFilterPeopleModal] = useState(false);
  const [currentPreview, setCurrentPreview] = useState();

  const { data: latestShareDataQuery } = useLatestShareData();
  const latestShareData = latestShareDataQuery?.data;
  const shareTypes = latestShareData?.types?.map((t) => t.shareType);

  const { pageData: pagedRecipients, paginationElement } = usePagination(
    userComm.recipients,
    { initialPageSize: 10, disablePageSizeSelector: true },
  );

  const { data: sendEmailJobRecords } = useSendEmailJobRecords(
    pagedRecipients,
    editor,
  );

  const sendEmailJobRecordsByRecipientId = sendEmailJobRecords.reduce(
    (acc, p) => ({ ...acc, [p.id]: p }),
    {},
  );

  const previewRecord = currentPreview?.record;

  return (
    <div className="flex flex-col gap-4">
      {showFilterPeopleModal && (
        <FilterPeopleModal
          excludeIds={recipientIds}
          onCancel={() => setShowFilterPeopleModal(false)}
          onSubmit={(people) => {
            const recipients = people.map((p) => ({
              id: p.id,
              type: p.type,
              email: p.email,
            }));

            updateUserComm({
              recipients: [...userComm.recipients, ...recipients],
            });
          }}
        />
      )}
      {previewRecord && (
        <EditorPreview
          title={
            <div className="flex items-center gap-4">
              <Avatar
                id={currentPreview.avatarId}
                type={currentPreview.avatarType}
                name={currentPreview.name}
              />{" "}
              {currentPreview.name}
            </div>
          }
          editor={editor}
          record={previewRecord}
          onCancel={() => setCurrentPreview(null)}
          placeholderFields={getPlaceholderFieldsConfig({ shareTypes })}
        />
      )}
      <div className="flex w-full gap-2">
        <Button
          className="flex-1"
          disabled={disabled}
          variant="outline"
          onClick={() => setShowFilterPeopleModal(true)}
          isLoading={
            isPendingUpdateUserComm &&
            updateUserCommVariables.recipients?.length >
              userComm.recipients.length
          }
        >
          <UserPlus /> {t("user_comms.actions.add_recipients.title")}
        </Button>
        <Button
          className="flex-1"
          variant="outline"
          onClick={async () => {
            (await confirm()) && updateUserComm({ recipients: [] });
          }}
          disabled={disabled || userComm.recipients.length === 0}
          isLoading={
            isPendingUpdateUserComm &&
            updateUserCommVariables.recipients?.length === 0
          }
        >
          <X /> {t("user_comms.actions.clear_recipients.title")}
        </Button>
      </div>

      <ItemList
        columnSizing="1fr 60px"
        zeroStateRenderer={() => (
          <div>{t("user_comms.recipients_list.zero_state")}</div>
        )}
      >
        {pagedRecipients.map((r) => {
          const sendEmailJobRecord =
            sendEmailJobRecordsByRecipientId[r.id] ?? {};

          return (
            <ItemListRow key={r.id}>
              <ItemListColumn.Avatar
                id={sendEmailJobRecord.avatarId}
                name={sendEmailJobRecord.name}
                extra={
                  sendEmailJobRecord.email ?? (
                    <div className="text-semibold flex items-center gap-1 text-warning">
                      <TriangleAlert size={16} />{" "}
                      {t("user_comms.email_missing")}
                    </div>
                  )
                }
                avatarType={sendEmailJobRecord.avatarType}
              />
              <ItemListColumn>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPreview(sendEmailJobRecord)}
                  title={t("minutes.preview")}
                >
                  <Eye />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={disabled}
                  title={t("user_comms.actions.remove_recipient.title")}
                  onClick={() =>
                    updateUserComm({
                      recipients: userComm.recipients.filter(
                        (rec) => rec.id !== r.id,
                      ),
                    })
                  }
                >
                  <X />
                </Button>
              </ItemListColumn>
            </ItemListRow>
          );
        })}
      </ItemList>
      {paginationElement}
      {dialogElement}
    </div>
  );
};

export default UserCommRecipientList;
